/**
 * Shared ECharts building blocks, so every chart in the panel carries the same
 * marks and the same restraint.
 *
 * The mark specs are fixed: bars ≤ 22px with a 4px rounded cap on the end of the
 * stack, 2px lines with ≥ 8px markers ringed in the surface colour, area fills
 * as a gradient that reaches zero at the baseline, and hairline solid gridlines
 * one step off the surface. Text always wears text tokens — never a series
 * colour.
 *
 * Proportions OUTSIDE a plot are not drawn here: a share of a whole is a Meter,
 * plain CSS. A chart library is for data with an axis, and four ECharts
 * instances for four percentages was four canvases doing what four divs do
 * better.
 */

const FONT = '"Open Sans", "Droid Arabic Kufi", "Droid Sans", Arial, sans-serif'

/**
 * A canvas cannot read a custom property, so the palette is resolved from the
 * document at the moment an option is built, and cached until the theme moves.
 * The theme block in tokens.css is still the only place any of it is decided.
 *
 * All three modules that build chart options subscribe to the theme, so a
 * switch re-renders them, rebuilds their options against this palette and
 * redraws — which is why nothing here has to be reactive itself.
 */
const FALLBACK = {
  surface: '#ffffff',
  grid: '#eef2f6',
  axis: '#98a2b3',
  ink: '#3b465c',
  success: '#0f8a6d',
  failure: '#b42318',
  incomplete: '#d97706',
  absent: '#64748b',
  gold: '#c9a24b',
}

let cache = null
let cachedTheme = null

/** The live palette. The light values stand in outside a browser. */
export function palette() {
  if (typeof document === 'undefined') return FALLBACK
  const root = document.documentElement
  const theme = root.dataset.theme ?? 'light'
  if (cache && cachedTheme === theme) return cache
  const style = getComputedStyle(root)
  const read = (name, fallback) => style.getPropertyValue(name).trim() || fallback
  cachedTheme = theme
  cache = {
    surface: read('--bg-surface', FALLBACK.surface),
    grid: read('--chart-grid', FALLBACK.grid),
    axis: read('--chart-axis', FALLBACK.axis),
    ink: read('--text', FALLBACK.ink),
    success: read('--chart-success', FALLBACK.success),
    failure: read('--chart-failure', FALLBACK.failure),
    incomplete: read('--chart-incomplete', FALLBACK.incomplete),
    absent: read('--chart-absent', FALLBACK.absent),
    /* THE BRAND'S SECOND COLOUR. `--chart-gold` has been declared in tokens.css since the
       palette was written and read by nothing, so every chart drew in status hues — and a
       chart that is not about pass and fail had to borrow one. */
    gold: read('--chart-gold', FALLBACK.gold),
    /* Ink for a label drawn INSIDE a filled band. The fills lift toward white
       in dark, so white-on-fill stops working there and this goes dark. */
    onFill: read('--on-status', '#ffffff'),
  }
  return cache
}

/* Read the way the four constants these replaced did. Getters, so each access
   resolves against whatever theme is on screen when the option is built. */
const P = {
  get surface() {
    return palette().surface
  },
  get grid() {
    return palette().grid
  },
  get axis() {
    return palette().axis
  },
  get ink() {
    return palette().ink
  },
}

/**
 * The four programme outcomes. They mean pass/fail, so they take status colours
 * rather than categorical hues; the set is validated in tokens.css.
 *
 * `absent` is "nothing happened yet", not a fifth outcome competing with the
 * other three — it used to be a saturated indigo, and since it is also the
 * tallest bar in every programme it read as the chart's headline. A slate keeps
 * it legible and correctly quiet.
 */
export const OUTCOME_COLORS = {
  get success() {
    return palette().success
  },
  get failure() {
    return palette().failure
  },
  get incomplete() {
    return palette().incomplete
  },
  get absent() {
    return palette().absent
  },
}

/** Fixed order, since the object above has no own enumerable data keys. */
export const OUTCOME_KEYS = ['success', 'failure', 'incomplete', 'absent']

/* The three objects below are module-level, so a plain `color: P.axis` would
   resolve once at import and freeze the light palette into every chart for the
   life of the tab. As getters they resolve when ECharts reads them, which is
   after a theme switch has already re-pointed the variables. */
const baseText = {
  get color() {
    return P.axis
  },
  fontFamily: FONT,
  fontSize: 12,
}

/** `#rrggbb` at an alpha, for fills that have to fade into the surface. */
const alpha = (hex, a) => {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

/**
 * The wash under a line: the series colour at the stroke, gone by the baseline.
 *
 * A flat 10% fill was fine for one line and muddy for two — where the plots
 * crossed, two flat washes overprinted into a third colour that belonged to
 * neither series. A gradient that reaches zero puts the ink where the line is
 * and nothing where it is not, so overlaps stay readable.
 */
const areaFill = (color) => ({
  type: 'linear',
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
    { offset: 0, color: alpha(color, 0.26) },
    { offset: 1, color: alpha(color, 0) },
  ],
})

export const tooltip = (trigger = 'axis') => ({
  trigger,
  axisPointer: { type: trigger === 'axis' ? 'line' : 'none', lineStyle: { color: P.grid } },
  backgroundColor: P.surface,
  borderColor: P.grid,
  borderWidth: 1,
  padding: [10, 12],
  /* The tooltip is a real DOM node ECharts styles inline, so this one cannot be
     a token — the shadow is written for whichever theme is on screen. */
  extraCssText: `box-shadow: 0 8px 28px rgba(${
    palette().surface === '#ffffff' ? '15,23,41,.1' : '0,0,0,.55'
  }); border-radius: 10px;`,
  textStyle: { color: P.ink, fontFamily: FONT, fontSize: 12 },
})

export const legend = {
  bottom: 0,
  icon: 'circle',
  itemWidth: 8,
  itemHeight: 8,
  itemGap: 18,
  textStyle: {
    get color() {
      return P.ink
    },
    fontFamily: FONT,
    fontSize: 12,
  },
}

/* `containLabel` is deprecated in ECharts 6 and logs on every render; the docs
   give this pair as its exact equivalent. */
export const grid = {
  top: 24,
  left: 8,
  right: 8,
  bottom: 44,
  outerBoundsMode: 'same',
  outerBoundsContain: 'axisLabel',
}

/**
 * Wraps a long category name onto a second line at a word boundary instead of
 * cutting it at `max`. Programme names here run to 38 characters — truncated to
 * one line, four of the seven read as the same "…برنامج التوعية بالأمن", which
 * is the one thing an axis label has to avoid. Only past two lines does it
 * ellipsise.
 */
const wrapLabel = (value, max) => {
  if (value.length <= max) return value
  const words = value.split(' ')
  const lines = ['']
  for (const word of words) {
    const line = lines.length - 1
    if (!lines[line]) lines[line] = word
    else if (`${lines[line]} ${word}`.length <= max) lines[line] += ` ${word}`
    else if (lines.length < 2) lines.push(word)
    else {
      lines[line] = `${lines[line].slice(0, max - 1)}…`
      break
    }
  }
  return lines.join('\n')
}

/**
 * `maxLabel` wraps long category names rather than letting them collide or be
 * clipped; the tooltip still carries the full name.
 */
export const categoryAxis = (data, { maxLabel } = {}) => ({
  type: 'category',
  data,
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: {
    ...baseText,
    hideOverlap: true,
    ...(maxLabel
      ? { lineHeight: 15, formatter: (value) => wrapLabel(String(value), maxLabel) }
      : null),
  },
})

export const valueAxis = {
  type: 'value',
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: { ...baseText, formatter: (value) => value.toLocaleString('en-US') },
  /* Dashed. A solid rule reads as a divider — something the plot is separated
     BY — where a dash reads as a measuring line, which is what it is. */
  splitLine: {
    lineStyle: {
      get color() {
        return P.grid
      },
      width: 1,
      type: 'dashed',
    },
  },
}

/**
 * Column series: capped width, square at the baseline, 4px rounded cap on the
 * topmost segment only.
 *
 * `stack` groups segments into one column — the right call when one series dwarfs
 * the others, because side-by-side would flatten the small ones to nothing. The 2px
 * separation between segments is a surface-coloured border, never a stroke in the
 * series colour.
 */
export const barSeries = (
  name,
  data,
  color,
  { stack, cap = false, horizontal = false, label = false } = {},
) => ({
  name,
  type: 'bar',
  data,
  stack,
  barMaxWidth: 26,
  barCategoryGap: '34%',
  barGap: '18%',
  itemStyle: {
    color,
    /* The cap belongs on the END of the stack, which is the top of a column and
       the far side of a horizontal bar. Ungrouped columns round both top
       corners; a stack rounds only its last segment. */
    borderRadius: stack ? (cap ? (horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]) : 0) : [4, 4, 0, 0],
    ...(stack ? { borderColor: P.surface, borderWidth: 2 } : null),
  },
  ...(label
    ? {
        label: {
          show: true,
          position: 'top',
          fontFamily: FONT,
          fontSize: 11,
          fontWeight: 600,
          color: P.ink,
          /* Zero is the commonest value in this data and the least worth
             printing — seven programmes with three empty outcomes each would put
             twenty-one noughts on the plot. */
          formatter: ({ value }) => (value ? value.toLocaleString('en-US') : ''),
        },
      }
    : null),
  emphasis: { focus: 'series' },
})

/** Line series: 2px stroke, ringed marker, and a wash that fades to nothing. */
export const lineSeries = (name, data, color, { area = true } = {}) => ({
  name,
  type: 'line',
  data,
  smooth: 0.25,
  symbol: 'circle',
  symbolSize: 8,
  showSymbol: data.length <= 24,
  lineStyle: { width: 2, cap: 'round', join: 'round', color },
  itemStyle: { color, borderColor: P.surface, borderWidth: 2 },
  /* Hovering one series dims the other, which is the only way to follow a
     single plot through a couple of hundred daily points. */
  emphasis: { focus: 'series' },
  blur: { lineStyle: { opacity: 0.18 }, areaStyle: { opacity: 0.18 } },
  ...(area ? { areaStyle: { color: areaFill(color) } } : null),
})

/**
 * Two lines, and the row height that has to go with them.
 *
 * A wrapped axis label grows downward without ECharts reserving any space for
 * it, and it is centred on its own tick — so a three-line name spills half a
 * line into the row above and half into the row below. On the paths ranking that
 * meant two programme names printed on top of each other and one row losing its
 * label entirely. Capped at two lines, every row is the same height and the axis
 * can be given a matching one.
 */
const LABEL_LINE = 15
const LABEL_LINES = 2
export const AXIS_LABEL_HEIGHT = LABEL_LINE * LABEL_LINES

/** The row height a horizontal plot needs: two lines of label, plus air. */
export const rowHeight = (rows) => Math.max(220, rows * (AXIS_LABEL_HEIGHT + 14) + 56)

export const horizontalAxes = (names, { labelChars = 16, labelWidth = 150, max } = {}) => ({
  /* Zero at the READING START. A horizontal bar is a sentence — it begins where
     the reader begins — and left-to-right growth in an Arabic layout runs
     backwards under labels that are themselves right-ranged. */
  xAxis: { ...valueAxis, inverse: true, ...(max == null ? null : { max }) },
  yAxis: {
    type: 'category',
    data: names,
    /* ECharts runs a category axis bottom-up; inverting puts the first row at
       the top, which is where an ordered list is read from. */
    inverse: true,
    /* With the bars starting on the right, the names belong on the right too —
       beside the head of each bar rather than across the plot from it. */
    position: 'right',
    axisLine: { show: false },
    axisTick: { show: false },
    /*
     * The name column: WRAPPED BY THE FORMATTER, RESERVED BY THE WIDTH.
     *
     * Those are two different jobs and the bug was doing only the first. The
     * formatter breaks the string at a word boundary and ellipsises past its
     * second line, which is right — but with no width declared, and the axis at
     * `position: 'right'`, nothing reserved room for the result: the label was
     * drawn past the canvas edge and CLIPPED, so `إدارة المشاريع والتحول` came
     * out as `رة المشاريع والتحول`, missing its first word, silently, and only
     * for the longest names.
     *
     * `width` reserves the column and `grid.right` steps the plot back by the
     * same amount — they have to be the same number, which is why the caller
     * passes one constant to both.
     *
     * `overflow: 'truncate'` rather than `'break'`: breaking by width wraps the
     * ALREADY-wrapped string a second time, and two wrappers put these names on
     * three lines, which overlapped the row below because `rowHeight()` budgets
     * two. One wrapper, one reservation.
     */
    axisLabel: {
      ...baseText,
      lineHeight: LABEL_LINE,
      width: labelWidth,
      overflow: 'truncate',
      formatter: (value) => wrapLabel(String(value), labelChars),
    },
  },
})

/**
 * The four programme outcomes as one ring.
 *
 * A ring rather than the flat 100% bar this replaced: the bar was honest but it
 * was also the third horizontal bar on a card that already carries a stacked bar
 * plot, and three bar treatments in one card is one visual idea doing three
 * jobs. A ring is the same reading in a form the page does not otherwise use,
 * and it leaves its own middle free for the number the card is really about.
 *
 * No labels on the slices — three of the four are slivers here, and ECharts
 * would run leader lines out to them. The key beside the ring names them.
 */
export const donutOption = (shares, labels) => ({
  tooltip: {
    ...tooltip('item'),
    formatter: (params) => `${params.name}<br/><b>${params.value}%</b>`,
  },
  series: [
    {
      type: 'pie',
      radius: ['64%', '92%'],
      center: ['50%', '50%'],
      /* The data arrives in outcome order — pass, fail, incomplete, absent —
         which is a meaning, not a magnitude. Re-sorting would put the biggest
         slice first and quietly change what the chart is about. */
      sort: 'none',
      avoidLabelOverlap: false,
      label: { show: false },
      labelLine: { show: false },
      itemStyle: {
        borderColor: P.surface,
        borderWidth: 2,
        borderRadius: 3,
      },
      emphasis: { scaleSize: 4, itemStyle: { borderWidth: 2 } },
      data: OUTCOME_KEYS.map((key) => ({
        name: labels[key],
        value: shares[key],
        itemStyle: { color: OUTCOME_COLORS[key] },
      })),
    },
  ],
})

/* ═══════════════════════════════════════════════════════════════════════════
   Platform-KPI shapes — ported from the rawam-ui reference panel
   (features/platform-kpis). Three forms, each used more than once.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * The health bands behind the headline indicators.
 *
 * Deliberately different per indicator, and display-only: no figure is filtered
 * or recomputed from them. 80% policy acknowledgement is behind; 80% of staff
 * active in a month is excellent — one shared threshold would misread both.
 */
export const HEADLINE_THRESHOLDS = {
  activeRate: { warning: 60, danger: 30 },
  pathCompletionRate: { warning: 70, danger: 40 },
  averageExamScore: { warning: 70, danger: 50 },
  complianceRate: { warning: 85, danger: 60 },
}

/**
 * Which health band a score falls in — the name, not the hex.
 *
 * The KPI row draws its indicators in CSS rather than as charts now, so the band
 * has to be expressible as a tone the stylesheet knows. The thresholds stay here
 * because they are a property of the indicator, not of how it happens to be
 * drawn.
 */
export const headlineBand = (value, thresholds) => {
  if (value == null) return 'neutral'
  if (!thresholds) return 'success'
  if (value < thresholds.danger) return 'danger'
  if (value < thresholds.warning) return 'warning'
  return 'success'
}

/**
 * The participation funnel: how many of the targeted population reach each
 * stage. Every stage is a subset of the one before it, so the shape only ever
 * narrows — which is the whole reason it is drawn as a funnel and not as bars.
 */
export const funnelOption = (stages) => {
  /* The share each stage keeps of the population the funnel STARTED with. It is
     the whole question a funnel is asked — "how many of the targeted actually
     passed" — and it was the one thing the bands did not say. */
  const top = stages[0]?.value || 0
  const share = (value) => (top ? Math.round((value / top) * 100) : null)

  return {
    /* No tooltip. Every band already prints its own name and figure inside
       itself, so the popup only repeated them — while covering the band below,
       hiding the one thing it did not already say. */
    tooltip: { show: false },
    series: [
      {
        type: 'funnel',
        top: 8,
        bottom: 8,
        left: '6%',
        right: '6%',
        minSize: '34%',
        maxSize: '100%',
        sort: 'none',
        gap: 4,
        label: {
          position: 'inside',
          fontFamily: FONT,
          fontSize: 12,
          color: '#ffffff',
          formatter: (params) => {
            const pct = share(params.value)
            const head = `${params.name} · ${params.value.toLocaleString('en-US')}`
            // The first band IS the whole, so "100%" on it says nothing.
            //
            // FSI…PDI around the percentage: canvas text is laid out by the
            // browser's bidi algorithm, and `%` is a weak terminator, so in an
            // Arabic run it detached from its own digits and rendered as
            // "%بدأوا المحتوى · 39 · 30". Isolated, it stays with its number.
            return params.dataIndex === 0 || pct == null
              ? head
              : `${head} · ⁨${pct}%⁩`
          },
        },
        labelLine: { show: false },
        itemStyle: { borderWidth: 0, borderRadius: 4 },
        emphasis: { disabled: true },
        data: stages.map((stage, index) => ({
          name: stage.label,
          value: stage.value,
          /* One hue, stepped: the stages are one population narrowing, not four
             different subjects, so they share the accent and separate by depth. */
          itemStyle: {
            color: `color-mix(in srgb, ${OUTCOME_COLORS.success} ${100 - index * 16}%, #ffffff)`,
          },
        })),
      },
    ],
  }
}

/**
 * A ranking drawn as 100%-stacked horizontal bars: the achieved portion against
 * that row's own remaining population.
 *
 * A plain ranked bar of the RATE alone paints a two-person department at 100%
 * exactly like a four-hundred-person one, so the tooltip always carries the real
 * head-counts. `average` draws the organisation-wide rate as a benchmark, which
 * is what turns a ranking into a judgement: below the line is behind the
 * organisation, not merely lower than the row above.
 *
 * Rows arrive already ordered — this never re-sorts, so what a panel's title
 * promises is what it plots.
 */
/* The department/programme name column. Declared once: the axis reserves it
   and the grid steps back by it, and the two have to be the same number. */
const RANK_LABEL_WIDTH = 150

/* The unfilled part of a row. Recessive on purpose: the accent segment is what
   the eye should follow. */
const RANK_TRACK = '#eef2f6'

export const rankedBarsOption = (rows, { valueLabel, remainderLabel, average } = {}) => {
  /* No pre-truncation: the axis wraps now, so a name that used to be cut at 40
     characters — which was still enough to render two different programmes
     identically — is simply shown. */
  const labels = rows.map((row) => row.name)
  return {
    tooltip: {
      ...tooltip('axis'),
      formatter: (params) => {
        const row = rows[params[0].dataIndex]
        return [
          `<b>${row.name}</b>`,
          `${valueLabel}: ${row.value.toLocaleString('en-US')} / ${row.total.toLocaleString('en-US')}`,
          `${row.rate}%`,
        ].join('<br/>')
      },
    },
    /* The track's swatch is named here rather than taken from its series,
       which paints nothing now. */
    legend: {
      ...legend,
      bottom: 0,
      data: [valueLabel, { name: remainderLabel, itemStyle: { color: RANK_TRACK } }],
    },
    /* `top` is headroom for the benchmark label, which rides the TOP of its
       dashed line; at 8px it was drawn against the plot's ceiling and clipped. */
    grid: {
      top: 24,
      left: 8,
      right: RANK_LABEL_WIDTH + 12,
      bottom: 42,
    },
    ...(() => {
      const axes = horizontalAxes(labels, { max: 100, labelWidth: RANK_LABEL_WIDTH })
      return {
        xAxis: { ...axes.xAxis, axisLabel: { ...baseText, formatter: (value) => `${value}%` } },
        yAxis: axes.yAxis,
      }
    })(),
    series: [
      {
        name: valueLabel,
        type: 'bar',
        stack: 'rate',
        barMaxWidth: 18,
        /* Rounded on BOTH ends. A 100% row and a 4% row were the same square
           slab at different lengths; a capsule reads as a filled amount. */
        /* THE BRAND'S SECOND COLOUR, not the success green. This chart ranks departments by
           how much they took part; it says nothing about anyone passing. Drawn in the pass
           colour it made a claim the figures do not support, and it put a status hue on the
           one panel where every other mark IS a status. Gold is the token's own stated job —
           "indicators, never status". */
        itemStyle: { color: palette().gold, borderRadius: 9 },
        showBackground: true,
        backgroundStyle: { color: RANK_TRACK, borderRadius: 9 },
        /* Every row prints its own rate. A ranking whose figures are only
           available on hover makes the reader interrogate twelve rows one at a
           time to learn what it already knows. */
        /* The ink flips with the bar. A short capsule cannot contain its own
           label, so the figure ends up over the pale remainder track — and at
           0% the row printed white text on near-white and vanished. Rich-text
           styles are how a per-datum colour is expressed here; `labelLayout`
           can move a label but not recolour it. */
        label: {
          show: true,
          position: 'insideRight',
          distance: 8,
          fontFamily: FONT,
          fontSize: 11,
          fontWeight: 600,
          formatter: ({ dataIndex }) => {
            const { rate } = rows[dataIndex]
            return rate < 6 ? `{out|${rate}%}` : `{in|${rate}%}`
          },
          rich: {
            in: { color: palette().onFill, fontFamily: FONT, fontSize: 11, fontWeight: 600 },
            out: { color: P.ink, fontFamily: FONT, fontSize: 11, fontWeight: 600 },
          },
        },
        data: rows.map((row) => row.rate),
        ...(average == null
          ? null
          : {
              markLine: {
                symbol: 'none',
                silent: true,
                label: {
                  /* At the START of the line — the TOP of the plot — where there
                     is nothing else. At `end` it sat on the axis, and with the
                     organisation average at 7% it printed "7%" a few pixels from
                     the "0%" tick, so the chart appeared to label its own
                     baseline twice. */
                  position: 'start',
                  distance: 6,
                  formatter: `${average}%`,
                  fontFamily: FONT,
                  fontSize: 11,
                  fontWeight: 'bold',
                  color: P.ink,
                  backgroundColor: P.surface,
                  padding: [2, 4],
                  borderRadius: 4,
                },
                lineStyle: { color: P.ink, type: 'dashed', width: 1, opacity: 0.55 },
                data: [{ xAxis: average }],
              },
            }),
      },
      {
        name: remainderLabel,
        type: 'bar',
        stack: 'rate',
        barMaxWidth: 18,
        /* DRAWN AS THE VALUE SERIES' BACKGROUND, not as a second segment.

           Stacked, the two segments were both fully rounded, so every row met
           its neighbour rounded-end to rounded-end and pinched — two capsules
           laid end to end rather than one track with an amount filled in. And
           the join could not be squared off per series either, because the end
           that should stay round is the OUTER one, which swaps sides at 0% and
           at 100%.

           `showBackground` on the value series draws one full-width rounded
           track per category, under the fill, at every value — no join to
           show. This series survives only so the legend keeps its second
           entry; it paints nothing, and the legend takes its swatch colour
           from `legend.data` below instead of from here. */
        itemStyle: { color: 'transparent' },
        silent: true,
        data: rows.map((row) => Math.max(0, 100 - row.rate)),
      },
    ],
  }
}
